import {
  SearchableSelect,
  type SearchableSelectItem,
} from "@/components/molecules/searchable-select";
import { useForms } from "@/features/qualifications/hooks/use-qualification-forms";
import { FileTextIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useTranslation } from "@/features/utils/i18n";

interface FormSelectProps {
  variant?: "button" | "select";
  value: string; // Form ID
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  allowNone?: boolean;
  /** The "none" choice's label — "all forms" when the select is a filter. */
  noneLabel?: string;
  status?: "active" | "inactive" | "draft";
}

export function FormSelect({
  variant,
  value,
  onChange,
  placeholder,
  disabled = false,
  allowNone = false,
  noneLabel,
  status,
}: FormSelectProps) {
  const { t } = useTranslation();
  // Fetch forms
  const { data: forms, isLoading } = useForms({ status });

  // Convert to SearchableSelectItem format
  const items: SearchableSelectItem[] = useMemo(
    () =>
      (forms || []).map((form) => ({
        id: form.id,
        label: form.name,
        description: form.description,
        icon: <FileTextIcon className="h-4 w-4" />,
        data: form,
      })),
    [forms],
  );

  return (
    <SearchableSelect
      variant={variant}
      items={items}
      value={value}
      onChange={(v) => onChange(Array.isArray(v) ? v[0] || "" : v)}
      multiple={false}
      placeholder={placeholder ?? t("common.select.form.placeholder")}
      searchPlaceholder={t("common.select.form.search")}
      emptyText={t("common.select.form.empty")}
      isLoading={isLoading}
      disabled={disabled}
      allowNone={allowNone}
      noneOption={
        allowNone
          ? {
              id: "",
              label: noneLabel ?? t("common.select.form.none"),
              description: noneLabel
                ? undefined
                : t("common.select.form.noneDescription"),
              icon: <FileTextIcon className="h-4 w-4" />,
            }
          : undefined
      }
    />
  );
}
